import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["item", "applyValue", "type", "apiRef", "focusElementRef", "inputRef", "tabIndex", "isFilterActive", "clearButton", "headerFilterMenu", "slotProps"];
import * as React from 'react';
import PropTypes from 'prop-types';
import useId from '@mui/utils/useId';
import { useGridRootProps } from '@mui/x-data-grid';
import { getValueOptions, isMultiSelectColDef } from '@mui/x-data-grid/internals';
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function GridFilterInputMultipleMultiSelect(props) {
  const {
      item,
      applyValue,
      apiRef,
      focusElementRef,
      inputRef,
      tabIndex,
      clearButton,
      headerFilterMenu,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const id = useId();
  const labelId = useId();
  const rootProps = useGridRootProps();
  const resolvedColumn = apiRef.current.getColumn(item.field);
  const getOptionValue = resolvedColumn.getOptionValue;
  const getOptionLabel = resolvedColumn.getOptionLabel;
  const resolvedValueOptions = React.useMemo(() => {
    return getValueOptions(resolvedColumn) || [];
  }, [resolvedColumn]);
  const filterValues = React.useMemo(() => {
    if (!Array.isArray(item.value)) {
      return [];
    }
    return item.value.filter(val => resolvedValueOptions.some(option => getOptionValue(option) === val));
  }, [getOptionValue, item.value, resolvedValueOptions]);
  const handleChange = React.useCallback(event => {
    const value = event.target.value;
    applyValue(_extends({}, item, {
      value: typeof value === 'string' ? value.split(',') : value
    }));
  }, [applyValue, item]);
  if (!resolvedColumn || !isMultiSelectColDef(resolvedColumn)) {
    return null;
  }
  const label = slotProps?.root?.label ?? apiRef.current.getLocaleText('filterPanelInputLabel');
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [/*#__PURE__*/_jsx(rootProps.slots.baseSelect, _extends({
      fullWidth: true,
      id: id,
      labelId: labelId,
      label: label,
      multiple: true,
      value: filterValues,
      onChange: handleChange,
      renderValue: selected => selected.map(val => {
        const option = resolvedValueOptions.find(opt => getOptionValue(opt) === val);
        return option ? getOptionLabel(option) : val;
      }).join(', '),
      slotProps: {
        htmlInput: _extends({
          tabIndex,
          ref: focusElementRef ?? inputRef
        }, slotProps?.root?.slotProps?.htmlInput)
      }
    }, rootProps.slotProps?.baseSelect, other, slotProps?.root, {
      children: resolvedValueOptions.map(option => {
        const value = getOptionValue(option);
        return /*#__PURE__*/_createElement(rootProps.slots.baseSelectOption, _extends({}, rootProps.slotProps?.baseSelectOption, {
          native: false,
          key: value,
          value: value
        }), getOptionLabel(option));
      })
    })), headerFilterMenu, clearButton]
  });
}
process.env.NODE_ENV !== "production" ? GridFilterInputMultipleMultiSelect.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  apiRef: PropTypes.shape({
    current: PropTypes.object.isRequired
  }).isRequired,
  applyValue: PropTypes.func.isRequired,
  className: PropTypes.string,
  clearButton: PropTypes.node,
  disabled: PropTypes.bool,
  focusElementRef: PropTypes /* @typescript-to-proptypes-ignore */.oneOfType([PropTypes.func, PropTypes.object]),
  headerFilterMenu: PropTypes.node,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: (props, propName) => {
      if (props[propName] == null) {
        return null;
      }
      if (typeof props[propName] !== 'object' || props[propName].nodeType !== 1) {
        return new Error(process.env.NODE_ENV !== "production" ? `Expected prop '${propName}' to be of type Element` : _formatErrorMessage(49, propName));
      }
      return null;
    }
  })]),
  /**
   * It is `true` if the filter either has a value or an operator with no value
   * required is selected (for example `isEmpty`)
   */
  isFilterActive: PropTypes.bool,
  item: PropTypes.shape({
    field: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    operator: PropTypes.string.isRequired,
    value: PropTypes.any
  }).isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  slotProps: PropTypes.object,
  tabIndex: PropTypes.number,
  type: PropTypes.oneOf(['multiSelect'])
} : void 0;
export { GridFilterInputMultipleMultiSelect };